import { useQuery, UseQueryResult } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { formatNumber } from '../utils/formatters';

// Common props interface for all Customer Analytics components
export interface SectionProps {
  branchId?: number;
  timeRange?: string;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

// KPIs data interface
interface CustomerKPIsData {
  totalCustomers: number;
  activeCustomers: number;
  inactiveCustomers: number;
  singleOrderCustomers: number;
  twoThreeOrderCustomers: number;
  fourPlusOrderCustomers: number;
}

// Loyalty distribution interfaces
interface LoyaltyDistributionData {
  totalOrdersNonOrders: {
    purchaseLastTwoMonths: number;
    noPurchaseLastTwoMonths: number;
  };
  totalOrders: {
    oneOrder: number;
    twoThreeOrders: number;
    fourPlusOrders: number;
  };
  noOrdersLastTwoMonths: {
    oneOrder: number;
    twoThreeOrders: number;
    fourPlusOrders: number;
  };
  ordersLastTwoMonths: {
    oneOrder: number;
    twoThreeOrders: number;
    fourPlusOrders: number;
  };
}

// Customer report interfaces
interface CustomerReportItem {
  month: string;
  customerLost: number;
  customerInLastYear: number;
}

interface CustomerReportData {
  chartData: CustomerReportItem[];
}

// Average purchases interface
interface AveragePurchaseItem {
  month: string;
  totalCustomers: number;
  totalFacture: number;
  avgPurchase: number;
}

interface AveragePurchaseYoYItem {
  yearMonth: number;
  month: string;
  factureCount: number;
  customerCount: number;
  avgCustomerPurchase: number;
}

// Customer trends interface
interface CustomerTrendItem {
  month: string;
  oldCustomers: number;
  newCustomers: number;
  customerLostThismonth: number;
}

// Customer balance interface
interface CustomerBalanceItem {
  month: string;
  newCustomers: number;
  lostCustomers: number;
  netChange: number;
}

// Purchase percentages interface
interface PurchasePercentageItem {
  month: string;
  newCustomersCountPercent: number;
  oldCustomersCountPercent: number;
  newCustomersAmountPercent: number;
  oldCustomersAmountPercent: number;
}

// Hook for Customer KPIs
export const useCustomerKPIs = (branchId: number): UseQueryResult<CustomerKPIsData, Error> => {
  return useQuery({
    queryKey: ['customer-kpis', branchId],
    queryFn: async (): Promise<CustomerKPIsData> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/kpis`);
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!branchId
  });
};

// Hook for Loyalty Distribution
export const useCustomerLoyaltyDistribution = (branchId: number): UseQueryResult<LoyaltyDistributionData, Error> => {
  return useQuery({
    queryKey: ['customer-loyalty-distribution', branchId],
    queryFn: async (): Promise<LoyaltyDistributionData> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/loyalty-distribution`);
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Customer Report
export const useCustomerReport = (branchId: number, months: number = 12): UseQueryResult<CustomerReportData, Error> => {
  return useQuery({
    queryKey: ['customer-report', branchId, months],
    queryFn: async (): Promise<CustomerReportData> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/customer-report`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Average Purchases
export const useCustomerAveragePurchases = (branchId: number, months: number = 12): UseQueryResult<AveragePurchaseItem[], Error> => {
  return useQuery({
    queryKey: ['customer-average-purchases', branchId, months],
    queryFn: async (): Promise<AveragePurchaseItem[]> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/average-purchases`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Average purchases totals row interface
interface AveragePurchaseTotal {
  totalCustomers: number;
  totalFacture: number;
  avgPurchase: number;
}

// Hook for Average Purchases Totals Row (computed from stored procedure)
export const useCustomerAveragePurchasesTotal = (branchId: number, months: number = 12): UseQueryResult<AveragePurchaseTotal | null, Error> => {
  return useQuery({
    queryKey: ['customer-average-purchases-total', branchId, months],
    queryFn: async (): Promise<AveragePurchaseTotal | null> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/average-purchases-total`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Year-over-Year Average Purchases
export const useCustomerAveragePurchasesYoY = (branchId: number, months: number = 12): UseQueryResult<AveragePurchaseYoYItem[], Error> => {
  return useQuery({
    queryKey: ['customer-average-purchases-yoy', branchId, months],
    queryFn: async (): Promise<AveragePurchaseYoYItem[]> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/average-purchases-yoy`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Customer Trends
export const useCustomerTrends = (branchId: number, months: number = 12): UseQueryResult<CustomerTrendItem[], Error> => {
  return useQuery({
    queryKey: ['customer-trends', branchId, months],
    queryFn: async (): Promise<CustomerTrendItem[]> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/customer-trends`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Customer Balance
export const useCustomerBalance = (branchId: number, months: number = 12): UseQueryResult<CustomerBalanceItem[], Error> => {
  return useQuery({
    queryKey: ['customer-balance', branchId, months],
    queryFn: async (): Promise<CustomerBalanceItem[]> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/customer-balance`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Hook for Purchase Percentages
export const useCustomerPurchasePercentages = (branchId: number, months: number = 12): UseQueryResult<PurchasePercentageItem[], Error> => {
  return useQuery({
    queryKey: ['customer-purchase-percentages', branchId, months],
    queryFn: async (): Promise<PurchasePercentageItem[]> => {
      const response = await apiClient.get(`/customer-analytics/${branchId}/purchase-percentages`, {
        params: { months }
      });
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!branchId
  });
};

// Export types for use in components
export type {
  CustomerKPIsData,
  LoyaltyDistributionData,
  CustomerReportItem,
  CustomerReportData,
  AveragePurchaseItem,
  AveragePurchaseTotal,
  AveragePurchaseYoYItem,
  CustomerTrendItem,
  CustomerBalanceItem,
  PurchasePercentageItem
};
